import { useState, MouseEvent, useEffect, useCallback } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { Button } from 'UIPackage/MainModule/components/Button';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { IKnmInspectorType } from 'APIPackage/CatalogsModule/interfaces/IKnmInspectorType';
import { getCatalogKnmInspectorTypes } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import { adminCreateCatalogTypes, adminGetCatalogTypes, adminUpdateCatalogTypes } from '../actions/adminAction';
import styles from './AdminEditForm.module.scss';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';

type KnmInspectorType = Omit<IKnmInspectorType, 'id'>;

interface IKnmInspectorTypeEditFormProps {
  onBack: () => void;
}

interface IKnmInspectorTypeEditFormStoreProps {
  knmInspectorTypes: IKnmInspectorType[];
}

const KnmInspectorTypeEditFormWithoutStore = ({
  onBack,
  knmInspectorTypes,
}: IKnmInspectorTypeEditFormProps & IKnmInspectorTypeEditFormStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [selectedKnmInspectorType, setSelectedKnmInspectorType] = useState<IKnmInspectorType | undefined>();
  const [isNew, setIsNew] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);

  useEffect(() => {
    dispatch(adminGetCatalogTypes(CommonCatalogsNamesEnum.KNM_INSPECTOR_TYPES));
  }, [dispatch]);

  const onRowClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const { id } = event.currentTarget.dataset;
      const knmInspectorType = knmInspectorTypes.find(k => k.id.toString() === id);

      setSelectedKnmInspectorType(prev => (prev?.id === knmInspectorType?.id ? undefined : knmInspectorType));
    },
    [knmInspectorTypes],
  );

  const onCreate = useCallback(() => {
    setIsNew(true);
    setSelectedKnmInspectorType({
      id: -1,
      enabled: true,
      code: '',
      name: '',
    });
    setIsFormOpen(true);
  }, []);

  const onEdit = useCallback(() => setIsFormOpen(true), []);

  const onCancel = useCallback(() => {
    setIsNew(false);
    setSelectedKnmInspectorType(undefined);
    setIsFormOpen(false);
  }, []);

  const onSubmit = useCallback(
    (values: KnmInspectorType) => {
      if (isNew) {
        dispatch(adminCreateCatalogTypes({ data: values, catalogName: CommonCatalogsNamesEnum.KNM_INSPECTOR_TYPES }));
      } else {
        dispatch(
          adminUpdateCatalogTypes({
            data: { id: selectedKnmInspectorType!.id, ...values },
            catalogName: CommonCatalogsNamesEnum.KNM_INSPECTOR_TYPES,
          }),
        );
      }
      onCancel();
    },
    [dispatch, isNew, onCancel, selectedKnmInspectorType],
  );

  const renderForm = useCallback(
    ({ handleSubmit }: FormRenderProps<KnmInspectorType>) => (
      <form onSubmit={handleSubmit}>
        <Row>
          <FieldBlock label="Название" color="black">
            <TextInputField name="name" validate={makeRequired('Поле обязательно')} />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock label="Код" color="black">
            <TextInputField name="code" disabled={!isNew} />
          </FieldBlock>
        </Row>
        <Row>
          <CheckboxField id="enabled" name="enabled" label="Активен" />
        </Row>
        <ModalActions placement="left" padding="none">
          <Button color="primary" text="Сохранить" id="saveButton" type="submit" variant="contained" />
          <Button color="default" text="Отмена" variant="contained" onClick={onCancel} />
        </ModalActions>
      </form>
    ),
    [isNew, onCancel],
  );

  if (isFormOpen) {
    return (
      <>
        <Form<KnmInspectorType> onSubmit={onSubmit} initialValues={selectedKnmInspectorType} render={renderForm} />
      </>
    );
  }

  return (
    <div>
      <div className={styles.Table}>
        <span className={styles.Header}>Тип проверяющего</span>
        {knmInspectorTypes &&
          knmInspectorTypes.map((knmInspType: IKnmInspectorType) => (
            <button
              key={knmInspType.id}
              className={selectedKnmInspectorType?.id === knmInspType.id ? styles.DataRowSelected : styles.DataRow}
              data-id={knmInspType.id}
              onClick={onRowClick}
              type="button"
            >
              {knmInspType.name}
            </button>
          ))}
      </div>
      <div className={styles.ControlButtons}>
        <Button
          className={styles.Button}
          color="default"
          type="button"
          variant="contained"
          onClick={onBack}
          text="Назад"
        />
        <Button
          className={styles.Button}
          color="primary"
          type="button"
          variant="contained"
          onClick={onCreate}
          text="Создать"
          id="createButton"
        />
        {selectedKnmInspectorType && (
          <Button
            className={styles.Button}
            color="default"
            type="button"
            variant="contained"
            onClick={onEdit}
            text="Редактировать"
          />
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: IRootState): IKnmInspectorTypeEditFormStoreProps => ({
  knmInspectorTypes: getCatalogKnmInspectorTypes(state),
});

export const KnmInspectorTypeEditForm = connect(mapStateToProps)(KnmInspectorTypeEditFormWithoutStore);
